// 2026-06-04 — Modal pra trocar a empresa vinculada à oportunidade.
// Caso B do "alterar CNPJ": user vinculou empresa errada na venda.
// Permite escolher outro cliente e (opcionalmente) propagar pro contrato
// gerado pela proposta.

import { useEffect, useState } from 'react';
import { Modal, Select, Checkbox, Alert, Typography, Space, message } from 'antd';
import axios from 'axios';
import { fetchAuthSession } from 'aws-amplify/auth';
import { API_URL } from '../../config';

const { Text } = Typography;

async function getToken(): Promise<string> {
  const s = await fetchAuthSession();
  return s.tokens?.idToken?.toString() ?? '';
}

interface ClienteSlim {
  id: number;
  razao_social: string;
  cnpj: string | null;
}

interface Props {
  propostaId: number;
  clienteAtualId: number;
  clienteAtualNome: string;
  onClose: () => void;
  onSaved: () => void;
}

export default function ModalTrocarCliente({
  propostaId, clienteAtualId, clienteAtualNome, onClose, onSaved,
}: Props) {
  const [clientes, setClientes] = useState<ClienteSlim[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [novoId, setNovoId] = useState<number | null>(null);
  const [propagar, setPropagar] = useState(true);
  const [salvando, setSalvando] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const token = await getToken();
        const r = await axios.get(`${API_URL}/comercial/clientes`, { headers: { Authorization: token } });
        const data = r.data.body ? JSON.parse(r.data.body) : r.data;
        setClientes(Array.isArray(data) ? data : (data.clientes || []));
      } catch (e: any) {
        message.error('Falha ao carregar clientes: ' + (e.response?.data?.error || e.message));
      } finally { setCarregando(false); }
    })();
  }, []);

  async function salvar() {
    if (!novoId) return;
    if (novoId === clienteAtualId) {
      message.warning('Você selecionou a mesma empresa atual');
      return;
    }
    setSalvando(true);
    try {
      const token = await getToken();
      const r = await axios.put(`${API_URL}/comercial/propostas/${propostaId}`,
        { action: 'trocar_cliente', cliente_id: novoId, propagar_contrato: propagar },
        { headers: { Authorization: token } });
      const d = r.data.body ? JSON.parse(r.data.body) : r.data;
      const vinc = d.contratos_vinculados || 0;
      const atu = (d.contratos_atualizados || []).length;
      const ign = (d.contratos_ignorados || []).length;
      const msg = [
        'Oportunidade atualizada.',
        vinc > 0 ? `${atu}/${vinc} contrato(s) também atualizado(s).` : null,
        ign > 0 ? `${ign} contrato(s) ignorado(s) por falta de permissão.` : null,
      ].filter(Boolean).join(' ');
      message.success(msg);
      onSaved();
    } catch (e: any) {
      message.error('Falha: ' + (e.response?.data?.error || e.message));
    } finally { setSalvando(false); }
  }

  const opcoes = clientes
    .filter(c => c.id !== clienteAtualId)
    .map(c => ({
      value: c.id,
      label: `${c.razao_social}${c.cnpj ? ` — ${c.cnpj}` : ''}`,
    }));

  return (
    <Modal open onCancel={onClose} title="Trocar empresa da oportunidade"
      okText="Trocar" cancelText="Cancelar"
      confirmLoading={salvando}
      okButtonProps={{ disabled: !novoId }}
      onOk={salvar} width={560}>
      <Space direction="vertical" size={12} style={{ width: '100%' }}>
        <Alert type="warning" showIcon
          message="Use quando vinculou a empresa errada na venda"
          description={
            <Text type="secondary" style={{ fontSize: 12 }}>
              Pra corrigir CNPJ/nome do cadastro atual, use o botão de Editar.
              Esta ação <strong>troca o vínculo</strong> da oportunidade pra outra empresa.
            </Text>
          } />
        <div>
          <Text strong style={{ fontSize: 12 }}>Empresa atual</Text>
          <div style={{ fontSize: 13, color: '#888' }}>{clienteAtualNome}</div>
        </div>
        <div>
          <Text strong style={{ fontSize: 12 }}>Nova empresa</Text>
          <Select
            showSearch
            optionFilterProp="label"
            value={novoId}
            onChange={setNovoId}
            placeholder={carregando ? 'Carregando…' : 'Pesquise por razão social ou CNPJ'}
            loading={carregando}
            options={opcoes}
            style={{ width: '100%', marginTop: 4 }} />
        </div>
        <div style={{ background: '#fafafa', padding: 10, borderRadius: 4 }}>
          <Checkbox checked={propagar} onChange={e => setPropagar(e.target.checked)}>
            <Text strong style={{ fontSize: 12 }}>Atualizar também o contrato gerado (recomendado)</Text>
          </Checkbox>
          <div style={{ fontSize: 11, color: '#888', marginLeft: 22, marginTop: 4 }}>
            Se a oportunidade já gerou contrato, ele será atualizado pra a nova empresa
            (e o contato principal zerado, já que pertence ao cadastro antigo).
            Desmarque só se você quer que o contrato continue na empresa atual de propósito.
          </div>
        </div>
      </Space>
    </Modal>
  );
}
